import { Injectable } from '@angular/core';
import { APIService } from './api.service';

/**
 * What geolocation had determined at the moment a prompt was shown.
 * `success` is tri-state on purpose — see setGeolocationContext.
 */
export interface GeolocationContext {
  success: boolean | null;
  state: string | null;
}

/**
 * Event types written to tbl_state_prompt_log. Mirrors the EVENT_* constants
 * on the server's TblStatePromptLog.
 *
 * Only the browser-side outcomes appear here: the lookups that reach the API
 * are logged by GeolocationController, which cannot be bypassed by a screen
 * that forgets to call this.
 */
export const GeoLogEvent = {
  BROWSER_DENIED: 'GEO_BROWSER_DENIED',
  BROWSER_FAILED: 'GEO_BROWSER_FAILED',
  BROWSER_UNSUPPORTED: 'GEO_BROWSER_UNSUPPORTED',
  PROFILE_FALLBACK: 'GEO_PROFILE_FALLBACK',
  STATE_BLOCKED: 'GEO_STATE_BLOCKED'
};

/**
 * Records every presentation of the State of Residence prompt.
 *
 * Why this exists: users reported being shown the prompt when they did not
 * expect it — their state was already on file, or geolocation should have
 * identified them as being in a non-compliant state. Nothing recorded that
 * the prompt appeared, or what the app knew at that moment, so those reports
 * could not be investigated. This captures it going forward.
 *
 * Deliberately a SEPARATE service rather than a method on
 * ProSubscriptionValidationService: that service imports
 * AgeVerificationModalComponent in order to open it, and the modal is what
 * calls this. Putting the logging there would create a circular import
 * between the two.
 */
@Injectable({
  providedIn: 'root'
})
export class StatePromptLogService {

  /**
   * Latest geolocation outcome, published by whichever screen ran it.
   *
   * Held here rather than passed through dialog data because the prompt is
   * opened from eight different call sites, only one of which currently has
   * the geolocation result to hand. Routing it through every one of them
   * would guarantee some are missed — and a row logged with "geolocation
   * unknown" when geolocation actually succeeded would actively mislead the
   * investigation.
   *
   * Starts as `null` success meaning "not attempted", which is a genuinely
   * different answer from `false` ("attempted and failed").
   */
  private geolocationContext: GeolocationContext = { success: null, state: null };

  constructor(private apiService: APIService) { }

  /** Called when a geolocation attempt resolves, successfully or not. */
  setGeolocationContext(success: boolean | null, state: string | null): void {
    this.geolocationContext = {
      success: success,
      state: (state && String(state).trim()) ? String(state).trim() : null
    };
  }

  getGeolocationContext(): GeolocationContext {
    return this.geolocationContext;
  }

  /**
   * Logs one presentation of the prompt. Call when it is SHOWN, not when it
   * is answered — a user who dismisses it is exactly the reported case.
   *
   * Fire-and-forget: the user id, timestamp, profile state and profile date
   * of birth are all resolved server-side, so there is nothing to send back,
   * and a logging failure must never interfere with the prompt the user is
   * looking at.
   */
  /**
   * Logs a geolocation outcome the SERVER cannot observe: a refused location
   * permission, a GPS timeout, a browser without navigator.geolocation, the
   * profile-state fallback, or the territory block that stops a user before
   * any prompt is raised.
   *
   * That last one is why this exists. Testing in QA from a non-compliant
   * state produced no rows at all - the block happens before the prompt - so
   * "the tester was correctly stopped" looked exactly like "the logging never
   * ran".
   *
   * Fire-and-forget, for the same reason logPromptShown is: a logging failure
   * must never disturb what the user is looking at.
   */
  logGeolocationEvent(eventType: string, opts?: {
    success?: boolean | null,
    state?: string | null,
    source?: string | null,
    detail?: string | null,
    promptSource?: string | null
  }): void {
    try {
      const o = opts || {};
      this.apiService.doApiCall(
        () => { /* fire and forget */ },
        'api/profile/state-prompt-log',
        'POST',
        {
          eventType: eventType,
          geolocationSuccess: (o.success === undefined) ? null : o.success,
          geolocationState: o.state || null,
          geolocationSource: o.source || null,
          eventDetail: o.detail || null,
          promptSource: o.promptSource || 'lobby'
        },
        null
      );
    } catch (e) {
      // Never let diagnostics break the page.
    }
  }

  logPromptShown(promptSource: string): void {
    try {
      const geo = this.geolocationContext;
      // NOTE: doApiCall on this branch takes 5 args and has no
      // errorCallback parameter (that exists only on the SG-177 branch). Its
      // internal error path simply never invokes the callback, which is
      // exactly the behaviour wanted here — a failed log is silent.
      this.apiService.doApiCall(
        () => { /* fire and forget */ },
        'api/profile/state-prompt-log',
        'POST',
        {
          geolocationSuccess: geo.success,
          geolocationState: geo.state,
          promptSource: promptSource || 'unknown'
        },
        null
      );
    } catch (e) {
      // Never let diagnostics break the prompt.
    }
  }
}
